import React, { useEffect, useState, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useSearchParams } from "react-router-dom";
import SelectInputElementComponent from "../../general/elements/inputElements/SelectInputElementComponent";
import AlertService from "../../../services/alerts/alert.service";
import accountActions from "../../../store/actions/accounts/account.actions";
import accountService from "../../../services/accounts/account.service";
import { businessCustomerActionTypes } from "../../../store/actionTypes/businessCustomer/businessCustomer.actionTypes";
import useBusinessCustomerListQuery from "../../../hooks/queries/useBusinessCustomerListQuery";
import useCustomersForAgentQuery from "../../../hooks/queries/useCustomersForAgentQuery";
import useAccount from "../../../hooks/useAccount";
import validationHelper from "../../../helpers/validation.helper";
import AccountErrorsEnum from "../../../enums/errors/accounts/accountErrors.enum";
import { SkeletonSelect } from "../../general/skeleton";
import useDelayedFlag from "../../../hooks/useDelayedFlag";

import "../../../styles/components/dialogs/accounts/addOrUpdateBusinessAccountsDialogComponent.scss";
import textInputModel from "../../../models/general/elements/inputElements/textInput.model";
import TextInputElementComponent from "../../general/elements/inputElements/TextInputElementComponent";

// 0007: a business account is a login (ApplicationUser) linked to a Rivhit company
// (BusinessCustomer, read-only). Several PEOPLE can share one company, each a separate login with
// their own person details (first/last/phone) stored on the ApplicationUser — NOT on the Rivhit
// record. ADD mode captures email/password/company + person details. EDIT mode (id query param)
// edits ONLY the person details; email/password/linked company are intentionally not editable
// here (per product decision), and nothing is ever written to Rivhit.
const AddOrUpdateBusinessAccountsDialogComponent = ({ accountActions, businessCustomers }) => {
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("id");
  const isEditMode = !!accountId;
  const { userInfo } = useAccount(); // M6: role decides which customer-list endpoint feeds the dropdown
  const dispatch = useDispatch();

  const [credentails, setCredentails] = useState({
    email: "",
    password: "",
    businessCustomerId: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState({});

  // 0016: the customer-list fetch now goes through the shared React Query hooks (deduped + cached),
  // and the result is bridged into the SAME businessCustomerReducer slice via FETCH_CUSTOMERS_SUCCESS
  // so the dropdown keeps reading `businessCustomers` from Redux via mapStateToProps — unchanged.
  // M6 role gate preserved: an Agent must use the assignment-scoped /ForAgent (the Administrator-only
  // /List would 403 for an agent); only the role-appropriate query is enabled.
  const isAgent = userInfo?.userType === "Agent";
  const knownRole = !!userInfo?.userType;
  const adminQuery = useBusinessCustomerListQuery({ enabled: knownRole && !isAgent });
  const agentQuery = useCustomersForAgentQuery({ enabled: knownRole && isAgent });
  const customersLoading = isAgent ? agentQuery.isLoading : adminQuery.isLoading;
  const showCustomerSkeleton = useDelayedFlag(customersLoading);

  useEffect(() => {
    const data = isAgent ? agentQuery.data : adminQuery.data;
    if (data) {
      dispatch({ type: businessCustomerActionTypes.FETCH_CUSTOMERS_SUCCESS, data });
    }
  }, [isAgent, adminQuery.data, agentQuery.data, dispatch]);

  // Edit mode: there is no GetAccountById endpoint, so prefill from the business-accounts list.
  // Fetched directly via the service (deps stay primitive to avoid a refetch loop).
  const loadAccount = useCallback(async () => {
    if (!isEditMode) return;
    try {
      const response = await accountService.getUsersByBusinessCustomer();
      if (!validationHelper.validateHttpResponse(response.data)) return;
      const account = (response.data.payload || []).find((a) => a.id === accountId);
      if (!account) {
        AlertService.error("החשבון לא נמצא", "ייתכן שהחשבון נמחק");
        return;
      }
      setCredentails((prev) => ({
        ...prev,
        email: account.email || "",
        firstName: account.firstName || "",
        lastName: account.lastName || "",
        phoneNumber: account.phoneNumber || "",
      }));
    } catch (error) {
      AlertService.error("טעינת פרטי החשבון נכשלה", "אנא נסה שנית מאוחר יותר");
      console.error("Error loading business account:", error);
    }
  }, [isEditMode, accountId]);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCredentails({ ...credentails, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    // Email/password/company are only set at creation. Person details are optional in both modes.
    if (!isEditMode) {
      if (!validationHelper.validateEmail(credentails.email)) {
        newErrors.email = "כתובת דואר אלקטרוני לא תקינה";
      }
      if (!validationHelper.validatePassword(credentails.password)) {
        newErrors.password = "הסיסמה לא תקינה, על הסיסמה להיות לפחות 6 תווים";
      }
      if (!credentails.businessCustomerId) {
        newErrors.businessCustomerId = "יש לבחור לקוח עסקי";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getRegisterErrorMessage = (errorCode) => {
    switch (errorCode) {
      case AccountErrorsEnum.EmailAlreadyExists:
        return "כתובת הדואר האלקטרוני כבר קיימת";
      case AccountErrorsEnum.BusinessCustomerNotFound:
        return "לקוח עסקי לא נמצא";
      default:
        return "שגיאה לא ידועה";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    try {
      if (isEditMode) {
        const response = await accountService.updateBusinessAccount(accountId, {
          firstName: credentails.firstName,
          lastName: credentails.lastName,
          phoneNumber: credentails.phoneNumber,
        });
        if (!validationHelper.validateHttpResponse(response.data)) {
          setErrors({ general: "שגיאה בעדכון פרטי החשבון" });
          return;
        }
        AlertService.success("פרטי החשבון עודכנו בהצלחה");
        return;
      }

      const response = await accountActions.registerBusiness(credentails);
      if (!validationHelper.validateHttpResponse(response, true)) {
        setErrors({ general: getRegisterErrorMessage(response?.error?.code) });
        return;
      }

      AlertService.success("החשבון העסקי נוצר בהצלחה");
      setCredentails({ email: "", password: "", businessCustomerId: "", firstName: "", lastName: "", phoneNumber: "" });
    } catch (error) {
      AlertService.error(isEditMode ? "חלה שגיאה בעדכון פרטי החשבון" : "חלה שגיאה ביצירת החשבון העסקי");
      console.error("Error saving business account:", error);
    }
  };

  const getBusinessCustomerSelectElement = () => {
    const options = businessCustomers.map((customer) => ({
      value: customer.customerId,
      label: `${customer.lastName} - ${customer.customerId}`,
    }));

    const inputProps = {
      name: "businessCustomerId",
      label: "לקוחות עסקיים",
      value: credentails.businessCustomerId,
      options,
      onChange: handleInputChange,
      searchable: true, // Enable search
    };

    return <SelectInputElementComponent props={inputProps} />;
  };

  const getTextInputElement = (name, type, placeholder) => {
    const inputProps = {
      ...textInputModel,
      name,
      type,
      error: errors[name],
      value: credentails[name],
      placeholder,
      onChange: handleInputChange,
    };
    return <TextInputElementComponent props={inputProps} />;
  };

  return (
    <div className="dialogContainer addOrUpdateBusinessAccountsDialogContainer">
      <h2>{isEditMode ? "עריכת פרטי חשבון עסקי" : "יצירת חשבון עסקי"}</h2>
      <form onSubmit={handleSubmit}>
        {/* Login credentials + company are set at creation only. */}
        {!isEditMode && getTextInputElement("email", "email", "הכנס דואר אלקטרוני")}
        {!isEditMode && getTextInputElement("password", "password", "הכנס סיסמה")}

        {isEditMode && credentails.email && (
          <div className="readonlyField">
            <span className="readonlyLabel">דוא"ל:</span>
            <span className="readonlyValue">{credentails.email}</span>
          </div>
        )}

        {/* Person details — live on the login account, editable in both modes. */}
        {getTextInputElement("firstName", "text", "הכנס שם פרטי")}
        {getTextInputElement("lastName", "text", "הכנס שם משפחה")}
        {getTextInputElement("phoneNumber", "text", "הכנס מספר טלפון")}

        {!isEditMode &&
          (customersLoading ? (
            showCustomerSkeleton && <SkeletonSelect />
          ) : (
            <>
              <div className="formGroup">{getBusinessCustomerSelectElement()}</div>
              {errors.businessCustomerId && <span className="error">{errors.businessCustomerId}</span>}
            </>
          ))}

        <button type="submit" className="primaryButton">
          שמור
        </button>
        {errors.general && <span className="error">{errors.general}</span>}
      </form>
    </div>
  );
};

const mapStateToProps = (state) => ({
  businessCustomers: state.businessCustomerReducer?.businessCustomers,
});

const mapDispatchToProps = (dispatch) => {
  return {
    accountActions: bindActionCreators(accountActions, dispatch),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(AddOrUpdateBusinessAccountsDialogComponent);
